import { DateTime } from 'luxon';

import Granularity from '@/common/enums/granularity.enum';
import { toServerDate } from '@/globals';
import Validatable, { IExtraValidation } from '@/validator/Validatable';
import i18n from '@/vueI18n';

export interface IAudienceLookBackWindowAPI {
  candidate_frequency: number;
  period_type: Granularity;
  time_period: number;
  to_date: string;
  from_date: string;
  relative: boolean;
  audiencerule_id: number;
  audiencelookbackwindow_id: number;
}

export default class AudienceLookBackWindow extends Validatable {
  constructor(
    public id: number = -1,
    public audienceRuleID?: number,
    public isRelative = true,
    public fromDate?: DateTime,
    public toDate?: DateTime,
    public timePeriod: number = 3,
    public periodType: Granularity.DAY | Granularity.MONTH | Granularity.YEAR = Granularity.MONTH,
    public candidateFrequency = 1,
  ) {
    super();
  }

  get description(): string {
    if (this.isRelative) {
      const periodType = i18n.global.t(`options.audienceLookBackWindowPeriodType.${this.periodType.toLowerCase()}s`);
      return i18n.global.t('messages.relativeTimePeriodTemplate', [`${this.timePeriod} ${periodType}`]);
    }

    return `${this.fromDate.toLocaleString(DateTime.DATE_SHORT)} ${i18n.global.t('adverbs.to')} ${this.toDate.toLocaleString(DateTime.DATE_SHORT)}`;
  }

  static fromApi(apiObject: IAudienceLookBackWindowAPI): AudienceLookBackWindow {
    return new AudienceLookBackWindow(
      apiObject.audiencelookbackwindow_id,
      apiObject.audiencerule_id,
      apiObject.relative,
      apiObject.from_date !== null ? DateTime.fromISO(apiObject.from_date) : null,
      apiObject.to_date !== null ? DateTime.fromISO(apiObject.to_date) : null,
      apiObject.time_period,
      apiObject.period_type,
      apiObject.candidate_frequency,
    );
  }

  static fromObject(obj) {
    return super.fromObject(
      obj,
      new AudienceLookBackWindow(
        obj.id,
        obj.audienceRuleID,
        obj.isRelative,
        obj.fromDate !== null ? DateTime.fromISO(obj.fromDate) : null,
        obj.toDate !== null ? DateTime.fromISO(obj.toDate) : null,
        obj.timePeriod,
        obj.periodType,
        obj.candidateFrequency,
      ),
    );
  }

  toApi(): IAudienceLookBackWindowAPI {
    return {
      audiencelookbackwindow_id: this.id >= 0 ? this.id : undefined,
      audiencerule_id: this.audienceRuleID,
      relative: this.isRelative,
      from_date: toServerDate(this.fromDate),
      to_date: toServerDate(this.toDate),
      time_period: this.timePeriod,
      period_type: this.periodType,
      candidate_frequency: this.candidateFrequency,
    };
  }

  getExtraValidationRules() {
    let rules: Record<string, IExtraValidation>;
    if (this.isRelative) {
      rules = {
        timePeriod: { value: this.timePeriod, rules: 'required|numeric:int|min_value:1' },
        periodType: { value: this.periodType, rules: 'required|max:500|not_empty_or_whitespace' },
      };
    } else {
      rules = {
        fromDate: { value: this.fromDate, rules: 'required|before_or_equal:toDate' },
        toDate: { value: this.toDate, rules: 'after_or_equal:fromDate|required' },
      };
    }
    return rules;
  }

  static validatorCreateRules() {
    return {
      candidateFrequency: 'required|numeric:int|min_value:1',
    };
  }
}
